<template>
  <div class="tl-item" :class="[`tl-item--${itemColor}`]">
    <div class="tl-item__node">
      <div class="tl-item__icon-box">
        <i :class="resolvedIcon"></i>
      </div>
    </div>

    <div class="tl-item__card">
      <div class="tl-item__top">
        <span class="tl-item__title">{{ title }}</span>
        <span v-if="tag || primaryBadge" class="tl-item__tag">
          {{ tag || primaryBadge }}
        </span>
      </div>

      <div class="tl-item__bottom">
        <span v-if="dateTime" class="tl-item__time">
          <i class="bi bi-clock"></i> {{ formattedDateTime }}
        </span>
        <span v-if="description" class="tl-item__desc">
          {{ description }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TimelineItem",
  props: {
    color: {
      type: String,
      default: "danger",
    },
    icon: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "",
    },
    dateTime: {
      type: String,
      default: "",
    },
    tag: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    badges: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    itemColor() {
      if (this.color) return this.color;
      return "danger";
    },
    primaryBadge() {
      if (this.badges && this.badges.length > 0) return this.badges[0];
      return "";
    },
    formattedDateTime() {
      if (!this.dateTime) return "";
      return this.dateTime.replace("–", "·");
    },
    resolvedIcon() {
      if (this.icon) {
        if (this.icon.includes("bi-")) return this.icon;
        if (this.icon.includes("fa-")) return this.icon;
      }
      return "bi bi-circle-fill";
    },
  },
};
</script>

<style scoped lang="scss">
.tl-item {
  --node-color: #f43f5e;
  --node-rgb: 244, 63, 94;

  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  &--danger {
    --node-color: #f43f5e;
    --node-rgb: 244, 63, 94;
  }
  &--warning {
    --node-color: #f59e0b;
    --node-rgb: 245, 158, 11;
  }
  &--primary,
  &--indigo {
    --node-color: #6366f1;
    --node-rgb: 99, 102, 241;
  }
  &--cyan,
  &--info {
    --node-color: #0ea5e9;
    --node-rgb: 14, 165, 233;
  }
  &--emerald,
  &--success {
    --node-color: #10b981;
    --node-rgb: 16, 185, 129;
  }
  &--purple {
    --node-color: #a855f7;
    --node-rgb: 168, 85, 247;
  }
}

.tl-item__node {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  align-self: stretch;

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    bottom: -8px;
    left: 50%;
    width: 2px;
    background: rgba(255, 255, 255, 0.08);
    transform: translateX(-50%);
    z-index: 1;
  }
}

.tl-item:first-child .tl-item__node::before {
  top: 50%;
}

.tl-item:last-child .tl-item__node::before {
  bottom: 50%;
}

.tl-item__icon-box {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  background: rgba(var(--node-rgb), 0.14);
  color: var(--node-color);
  border: 1px solid rgba(var(--node-rgb), 0.28);
  box-shadow: 0 2px 6px rgba(var(--node-rgb), 0.15);
  flex-shrink: 0;
  position: relative;
  z-index: 2;
}

.tl-item__card {
  flex: 1;
  min-width: 0;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid var(--app-border, rgba(255, 255, 255, 0.06));
  border-radius: 9px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(var(--node-rgb), 0.3);
  }
}

.tl-item__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.tl-item__title {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--app-heading, #f1f5f9);
  line-height: 1.3;
}

.tl-item__tag {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 5px;
  background: rgba(var(--node-rgb), 0.12);
  color: var(--node-color);
  border: 1px solid rgba(var(--node-rgb), 0.25);
  white-space: nowrap;
  letter-spacing: 0.2px;
  flex-shrink: 0;
}

.tl-item__bottom {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tl-item__time {
  font-size: 11px;
  font-weight: 500;
  color: var(--app-text, #94a3b8);
  display: inline-flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 10.5px;
  }
}

.tl-item__desc {
  font-size: 11px;
  color: var(--app-text, #94a3b8);
}

/* Light mode adjustments */
html[data-theme="light"] {
  .tl-item__node::before {
    background: #e2e8f0;
  }

  .tl-item__card {
    background: #f8fafc;
    border-color: #e2e8f0;

    &:hover {
      background: #f1f5f9;
    }
  }

  .tl-item__title {
    color: #0f172a;
  }

  .tl-item__time {
    color: #64748b;
  }
}
</style>
